import React, { useEffect, useState } from "react";
import { Dialog } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  getTelecallerUser,
  updateTelecallerLocation,
} from "../../store/slices/telecallerLeadSlice";
import toast from "react-hot-toast";
import { getStatesData } from "../../store/slices/kanbanSlice";
import LocationSelector from "../Users/LocationSelector";
import Button, { ButtonType } from "../Assets/Button";
import { ILeadUser } from "../../models/LeadData.model";
import { getUserId } from "../../utils/auth";

const TelecallerList: React.FC = () => {
  const dispatch = useDispatch<RootDispatch>();
  const { list, updatingLocations } = useSelector(
    (state: RootState) => state.telecallerLead
  );
  const loading = useSelector(
    (state: RootState) =>
      state.telecallerLead.loadingAndErrorState.fetchingTelecallerLead
  );
  const managerId = getUserId();
  const statesData = useSelector((state: RootState) => state.kanban.statesData);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<ILeadUser | null>(null);
  const [editData, setEditData] = useState<{
    states: string[];
    districts: string[];
  }>({ states: [], districts: [] });
  const [selectedState, setSelectedState] = useState("");
  const [, setSelectedDistrict] = useState("");

  useEffect(() => {
    if (managerId) dispatch(getTelecallerUser(managerId.toString()));
  }, [dispatch, managerId]);

  useEffect(() => {
    if (statesData.length === 0) {
      dispatch(getStatesData());
    }
  }, [dispatch, statesData.length]);

  const openEditModal = (user: ILeadUser) => {
    setCurrentUser(user);
    setEditData({
      states: [...(user.states || [])],
      districts: [...(user.districts || [])],
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentUser(null);
    setSelectedState("");
    setSelectedDistrict("");
  };

  const handleUpdate = () => {
    if (!currentUser) return;

    if (editData.states.length === 0) {
      toast.error("Please select at least one state");
      return;
    }
    if (editData.districts.length === 0) {
      toast.error("Please select at least one district");
      return;
    }

    dispatch(
      updateTelecallerLocation({
        userId: currentUser._id,
        states: editData.states,
        districts: editData.districts,
      })
    )
      .unwrap()
      .then(() => {
        toast.success("Locations updated successfully");
        closeModal();
      })
      .catch((err) => {
        toast.error(err || "Failed to update locations");
      });
  };

  if (selectedState) {
    setEditData({
      states: [selectedState],
      districts: [],
    });
    setSelectedDistrict("");
    setSelectedState("");
  }

  if (loading)
    return (
      <div className="flex justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />{" "}
      </div>
    );

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Username
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                States
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Districts
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {list.map((user) => (
              <tr key={user._id} className="hover:bg-gray-50">
                <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center space-x-2">
                  <svg
                    width="20px"
                    height="20px"
                    viewBox="0 0 28 28"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-2"
                    onClick={() => openEditModal(user)}
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      fill="#1C274C"
                      d="M11.9426 1.25L13.5 1.25C13.9142 1.25 14.25 1.58579 14.25 2C14.25 2.41421 13.9142 2.75 13.5 2.75H12C9.62178 2.75 7.91356 2.75159 6.61358 2.92637C5.33517 3.09825 4.56445 3.42514 3.9948 3.9948C3.42514 4.56445 3.09825 5.33517 2.92637 6.61358C2.75159 7.91356 2.75 9.62178 2.75 12C2.75 14.3782 2.75159 16.0864 2.92637 17.3864C3.09825 18.6648 3.42514 19.4355 3.9948 20.0052C4.56445 20.5749 5.33517 20.9018 6.61358 21.0736C7.91356 21.2484 9.62178 21.25 12 21.25C14.3782 21.25 16.0864 21.2484 17.3864 21.0736C18.6648 20.9018 19.4355 20.5749 20.0052 20.0052C20.5749 19.4355 20.9018 18.6648 21.0736 17.3864C21.2484 16.0864 21.25 14.3782 21.25 12V10.5C21.25 10.0858 21.5858 9.75 22 9.75C22.4142 9.75 22.75 10.0858 22.75 10.5V12.0574C22.75 14.3658 22.75 16.1748 22.5603 17.5863C22.366 19.031 21.9607 20.1711 21.0659 21.0659C20.1711 21.9607 19.031 22.366 17.5863 22.5603C16.1748 22.75 14.3658 22.75 12.0574 22.75H11.9426C9.63423 22.75 7.82519 22.75 6.41371 22.5603C4.96897 22.366 3.82895 21.9607 2.93414 21.0659C2.03933 20.1711 1.63399 19.031 1.43975 17.5863C1.24998 16.1748 1.24999 14.3658 1.25 12.0574V11.9426C1.24999 9.63423 1.24998 7.82519 1.43975 6.41371C1.63399 4.96897 2.03933 3.82895 2.93414 2.93414C3.82895 2.03933 4.96897 1.63399 6.41371 1.43975C7.82519 1.24998 9.63423 1.24999 11.9426 1.25ZM16.7705 2.27592C18.1384 0.908029 20.3562 0.908029 21.7241 2.27592C23.092 3.6438 23.092 5.86158 21.7241 7.22947L15.076 13.8776C14.7047 14.2489 14.4721 14.4815 14.2126 14.684C13.9069 14.9224 13.5761 15.1268 13.2261 15.2936C12.929 15.4352 12.6169 15.5392 12.1188 15.7052L9.21426 16.6734C8.67801 16.8521 8.0868 16.7126 7.68711 16.3129C7.28742 15.9132 7.14785 15.322 7.3266 14.7857L8.29477 11.8812C8.46079 11.3831 8.56479 11.071 8.7064 10.7739C8.87319 10.4239 9.07761 10.0931 9.31605 9.78742C9.51849 9.52787 9.7511 9.29529 10.1224 8.924L16.7705 2.27592ZM20.6634 3.33658C19.8813 2.55448 18.6133 2.55448 17.8312 3.33658L17.4546 3.7132C17.4773 3.80906 17.509 3.92327 17.5532 4.05066C17.6965 4.46372 17.9677 5.00771 18.48 5.51999C18.9923 6.03227 19.5363 6.30346 19.9493 6.44677C20.0767 6.49097 20.1909 6.52273 20.2868 6.54543L20.6634 6.16881C21.4455 5.38671 21.4455 4.11867 20.6634 3.33658ZM19.1051 7.72709C18.5892 7.50519 17.9882 7.14946 17.4193 6.58065C16.8505 6.01185 16.4948 5.41082 16.2729 4.89486L11.2175 9.95026C10.801 10.3668 10.6376 10.532 10.4988 10.7099C10.3274 10.9297 10.1804 11.1676 10.0605 11.4192C9.96337 11.623 9.88868 11.8429 9.7024 12.4017L9.27051 13.6974L10.3026 14.7295L11.5983 14.2976C12.1571 14.1113 12.377 14.0366 12.5808 13.9395C12.8324 13.8196 13.0703 13.6726 13.2901 13.5012C13.468 13.3624 13.6332 13.199 14.0497 12.7825L19.1051 7.72709Z"
                    />
                  </svg>
                  {user.username}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                  {user.states.join(", ")}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                  {user.districts.join(", ")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      <Dialog
        open={isModalOpen}
        onClose={closeModal}
        className="fixed  inset-0 z-50 flex items-center justify-center p-4"
      >
        <div className="fixed inset-0 bg-black opacity-30" />
        <Dialog.Panel className="bg-white overflow-auto  rounded-lg shadow-lg max-w-md w-full p-6 z-10">
          <Dialog.Title className="text-sm font-medium text-gray-900 mb-4">
            Edit Locations for {currentUser?.username}
          </Dialog.Title>

          {currentUser && (
            <LocationSelector
              role="telecaller"
              states={editData.states}
              districts={editData.districts}
              onStatesChange={(states) =>
                setEditData((prev) => ({
                  ...prev,
                  states,
                  districts: prev.districts.length ? prev.districts : [],
                }))
              }
              onDistrictsChange={(districts) =>
                setEditData((prev) => ({ ...prev, districts }))
              }
              statesData={statesData}
            />
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type={ButtonType.Cancel}
              onClick={closeModal}
              disabled={!selectedState}
              className="text-xs"
            >
              Cancel
            </Button>

            <Button
              type={ButtonType.Extra}
              onClick={handleUpdate}
              disabled={editData.states.length === 0 || updatingLocations}
              className="text-xs"
            >
              Update
            </Button>
          </div>
        </Dialog.Panel>
      </Dialog>
    </div>
  );
};

export default TelecallerList;
